import React from 'react'
import classNames from 'classnames'
import { Else, If, Then, When } from 'react-if'
import { ButtonVariant, LinkButton } from '@/atoms/Button'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { ChunkyPlusIcon } from '@/atoms/Icons/ChunkyPlusIcon'
import { GuildCopperIcon } from '@/atoms/Icons/GuildCopperIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsP, CaptionLG, CaptionMD, LabelSM, ParagraphMD, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { PriceDescriptor, PricePerInterval } from '@/molecules/GuildPricingLabel/GuildPricingLabel'
import { GuildSignupButtonNew } from '@/molecules/GuildSignupButton'
import { useGuildMember } from '@/services/GuildUserService'
import { ValidatedPromotion } from '@/services/SaleService'
import { GuildPlanSelectionNew } from '@/utils/GuildUtil/GuildUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

export interface GuildPlanBoxProps extends GuildPlanSelectionNew {
  className?: string
  offerId: string
  monthlyOfferId: string
  orderClass?: string
  planInterval?: 'monthly' | 'annual'
  punchy?: boolean
  title: string
  hideYearly?: boolean
  punchyButtonColors?: boolean
  disclosePriceInUSD?: boolean
  separateTierPlan?: boolean
  isSignupLayout?: boolean
}

export const GuildPlanBoxTiersStacked: React.FC<GuildPlanBoxProps> = ({
  className,
  currency = 'USD',
  monthlyOfferId,
  offerId,
  orderClass,
  price,
  monthlyPrice,
  tier,
  planInterval = 'annual',
  title,
  punchy,
  hideYearly,
  punchyButtonColors,
  disclosePriceInUSD = false,
  separateTierPlan,
  isSignupLayout,
}) => {
  const { t } = useTranslate('guild')
  const { isGuildMember } = useGuildMember()
  const { sale } = useGuildSignupContext()
  const planId = planInterval === 'annual' ? offerId : monthlyOfferId
  const promotion = sale?.promotions.find((p) => p.offer.id === planId)
  const monthlyPromotion = sale?.promotions.find((p) => p.offer.id === monthlyOfferId)

  const isPunchyAnnualPremium = punchy && planInterval === 'annual' && tier === 'premium'

  return (
    <div
      className={classNames('relative p-1 rounded-[20px] max-w-[380px]', orderClass, className, {
        'bg-black': isPunchyAnnualPremium,
      })}
    >
      <div
        className={classNames(
          'relative w-full h-full rounded-2xl bg-white text-core-gray-950 overflow-hidden md:flex flex-col text-left',
        )}
      >
        <div className="relative flex items-center justify-between gap-2 px-6 py-2">
          <ResponsiveBackgroundImage
            src={
              tier === 'basic'
                ? '/v1714422036/angel-studios/guild/card_banner-free'
                : '/v1714422036/angel-studios/guild/card_banner-paid'
            }
            responsiveAR={{
              xs: 4.2875,
              sm: 8,
              md: 8,
              lg: 4,
              xl: 4,
              '2xl': 4,
            }}
            transforms="w_372,h_55,c_fill"
            gravity="g_east"
          />
          <h3 className="relative">
            <TitleMD as={AsP} weight="bold" className="!leading-none">
              {title}
            </TitleMD>
            <When condition={separateTierPlan}>
              <ParagraphMD>
                <When condition={tier === 'basic'}>
                  <Translate t={t} i18nKey="streamWithLimitedAds">
                    Stream with limited ads
                  </Translate>
                </When>
                <When condition={tier === 'premium'}>
                  <Translate t={t} i18nKey="streamAdFree">
                    Stream Ad-Free
                  </Translate>
                </When>
              </ParagraphMD>
            </When>
          </h3>
          <GuildCopperIcon size={39} className="relative" shadow />
        </div>
        <div className="flex flex-col gap-3 p-6 pb-4">
          <ul
            className={classNames('flex flex-col gap-2', {
              'gap-[18px]': separateTierPlan,
            })}
          >
            <When condition={tier === 'basic'}>
              <When condition={!separateTierPlan}>
                <li className="mb-2">
                  <CaptionLG>
                    <Translate i18nKey="supportAngelAndOurShowsWithLimitedAdsV2" t={t}>
                      Stream our library with limited ads and help us continue to release more shows you&apos;ll love.
                    </Translate>
                  </CaptionLG>
                </li>
                <li className="relative mb-4 mt-2">
                  <hr className="border-gray-300" />
                </li>
              </When>
              <li className="flex items-center gap-2">
                <CheckIcon size={24} color="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="streamWithLimitedAds">
                    Stream with limited ads
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon size={24} color="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={tier === 'premium'}>
              <When condition={!separateTierPlan}>
                <li className="mb-2">
                  <CaptionLG>
                    <Translate i18nKey="supportAngelAndOurShowsAdFree" t={t}>
                      Stream Ad-Free, see every Angel Studios film in theaters and make the biggest impact on the future
                      of entertainment.
                    </Translate>
                  </CaptionLG>
                </li>
                <li className="relative mb-4 mt-2">
                  <hr className="border-gray-300" />
                  <LabelSM className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2">
                    PLUS
                  </LabelSM>
                </li>
              </When>
              <li className="flex items-center gap-2">
                <CheckIcon size={24} color="black" className="shrink-0 grow-0" />
                <When condition={separateTierPlan}>
                  <CaptionMD weight="bold">
                    <Translate t={t} i18nKey="everythingFromBasicPlusv2">
                      Everything you get from Basic plus
                    </Translate>
                    :
                  </CaptionMD>
                </When>
                <When condition={!separateTierPlan}>
                  <CaptionMD>
                    <Translate t={t} i18nKey="everythingFromBasic">
                      Everything you get from Basic
                    </Translate>
                  </CaptionMD>
                </When>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-6 shrink-0 grow-0">
                  <ChunkyPlusIcon size={20} color="black" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="streamAdFree">
                    Stream Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-6 shrink-0 grow-0">
                  <ChunkyPlusIcon size={20} color="black" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="twoTicketsToEveryReleasev4">
                    Two tickets to every Angel movie in theaters to use yourself or give away
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-6 shrink-0 grow-0">
                  <ChunkyPlusIcon size={20} color="black" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="discountOnMerchV3">
                    Save 20% on merch in our online store
                  </Translate>
                </CaptionMD>
              </li>
            </When>
          </ul>
        </div>
        <div className="mt-auto flex flex-col gap-2 px-6 pb-5">
          <When condition={separateTierPlan}>
            <When condition={tier === 'premium'}>
              <LinkButton
                href={isSignupLayout ? paths.guild.signup.interval.premium : paths.guild.interval.premium}
                variant="core-gray-900"
                className="mt-3 px-7 py-[17px]"
              >
                <LabelSM className="capitalize">{t('joinPremium', 'Join Premium')}</LabelSM>
              </LinkButton>
            </When>
            <When condition={tier === 'basic'}>
              <LinkButton
                href={isSignupLayout ? paths.guild.signup.interval.basic : paths.guild.interval.basic}
                variant="core-gray-900"
                className="mt-3 px-7 py-[17px]"
              >
                <LabelSM className="capitalize">{t('joinBasic', 'Join Basic')}</LabelSM>
              </LinkButton>
            </When>
          </When>
          <When condition={!separateTierPlan}>
            <PlanSelectorWell
              className="order-1"
              offerId={monthlyOfferId}
              price={price}
              monthlyPrice={monthlyPrice}
              tier={tier}
              currency={currency}
              planId={monthlyOfferId}
              promotion={monthlyPromotion}
              planInterval="monthly"
              isGuildMember={isGuildMember}
              punchyButtonColors={punchyButtonColors}
              disclosePriceInUSD={disclosePriceInUSD}
            />
            {!hideYearly && (
              <PlanSelectorWell
                className="order-0"
                offerId={offerId}
                price={price}
                monthlyPrice={monthlyPrice}
                tier={tier}
                currency={currency}
                planId={offerId}
                promotion={promotion}
                planInterval="annual"
                isGuildMember={isGuildMember}
                punchyButtonColors={punchyButtonColors}
                disclosePriceInUSD={disclosePriceInUSD}
              />
            )}
          </When>
        </div>
      </div>
      <When condition={isPunchyAnnualPremium}>
        <div
          className={classNames(
            'absolute rounded-full',
            separateTierPlan
              ? 'bg-warm-01 px-6 py-[6px] -top-3.5 left-1/2 -translate-x-1/2'
              : 'bg-warm-01 px-6 py-[6px] -top-3.5 right-6',
          )}
        >
          <CaptionMD weight="bold" color="white" className="text-[15px]">
            {sale?.abbreviatedPitch || t('bestValue', 'Best Value')}
          </CaptionMD>
        </div>
      </When>
    </div>
  )
}

interface PlanSelectorWellProps {
  offerId: string
  price: number
  monthlyPrice: number
  tier: 'premium' | 'basic'
  currency: string
  planId: string
  promotion?: ValidatedPromotion
  isGuildMember?: boolean
  planInterval: 'monthly' | 'annual'
  punchyButtonColors?: boolean
  disclosePriceInUSD?: boolean
  className?: string
}

function useButtonStyles({
  punchyButtonColors,
  tier,
  planInterval,
}: Pick<GuildPlanBoxProps, 'planInterval' | 'tier' | 'punchyButtonColors'>) {
  let buttonVariant: ButtonVariant = 'gray-900'
  let buttonClassName = ''
  const { experiments } = useGuildSignupContext()

  if (punchyButtonColors) {
    if (tier === 'premium') {
      const variant = experiments['guild-yearly-button-color-v2'] || 'black'

      if (planInterval === 'annual') {
        if (variant === 'warm-03') {
          buttonClassName = '!bg-warm-03 !border-warm-03 !text-black'
        } else if (variant === 'amazon') {
          buttonClassName = '!bg-[#FFD812] !border-[#FFD812] !text-black'
        } else if (variant === 'oxide') {
          buttonClassName = '!bg-core-oxide-bright !border-core-oxide-bright !text-black'
        }
      } else {
        buttonVariant = 'plain'
      }
    } else {
      buttonVariant = 'plain'
    }
  }

  return { buttonClassName, buttonVariant }
}

export const PlanSelectorWell: React.FC<PlanSelectorWellProps> = ({
  offerId,
  price,
  monthlyPrice,
  tier,
  currency,
  planId,
  promotion,
  planInterval,
  isGuildMember,
  punchyButtonColors,
  disclosePriceInUSD,
  className,
}) => {
  const { t } = useTranslate('guild')

  const { buttonClassName, buttonVariant } = useButtonStyles({ punchyButtonColors, tier, planInterval })

  return (
    <div className={classNames('flex rounded-lg bg-core-gray-100 p-4', className)}>
      <div>
        <If condition={planInterval === 'annual'}>
          <Then>
            <CaptionLG weight="semibold" className="uppercase">
              <Translate i18nKey="annual" t={t}>
                Annual
              </Translate>
            </CaptionLG>
          </Then>
          <Else>
            <CaptionLG weight="semibold" className="uppercase">
              <Translate i18nKey="monthly" t={t}>
                Monthly
              </Translate>
            </CaptionLG>
          </Else>
        </If>
        <div className="-mb-3">
          <PriceDescriptor
            currency={currency}
            price={price}
            monthlyPrice={monthlyPrice}
            planInterval={planInterval}
            offerId={offerId}
            tier={tier}
          />
        </div>
      </div>
      <div className="ml-auto">
        <PricePerInterval
          className="mb-2"
          offerId={offerId}
          planInterval={planInterval}
          price={price}
          monthlyPrice={monthlyPrice}
          tier={tier}
          disclosePriceInUSD={disclosePriceInUSD}
        />

        <GuildSignupButtonNew
          currency={currency}
          offerId={planId}
          price={price}
          promoCode={promotion ? promotion?.promotionCode.code : null}
          planInterval={planInterval}
          className={classNames('ml-auto !w-fit', buttonClassName)}
          buttonVariant={buttonVariant}
        >
          <LabelSM className="capitalize">
            <If condition={isGuildMember}>
              <Then>
                <Translate t={t} i18nKey="upgradeMembership">
                  Upgrade Membership
                </Translate>
              </Then>
              <Else>
                <Translate t={t} i18nKey="select">
                  Select
                </Translate>
              </Else>
            </If>
          </LabelSM>
        </GuildSignupButtonNew>
      </div>
    </div>
  )
}
